import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { useTheme } from 'styled-components';
import {
  X, Search, Bell, FileText, Sun, Moon, Sparkles, Send, Shield, Eye,
  LogOut, ChevronRight, ChevronLeft, Pencil, Plus, Users, Trash2, Download,
  Calendar, Upload, ClipboardList, Target, Award, GraduationCap, BarChart3,
  Play, Globe, Rocket, Mail, CreditCard, Lock, Copy, Clock, Layers, Route,
  Check, BookOpen, Settings, Flag, LayoutDashboard,
  ArrowDown, ArrowUp, GitBranch, Building2, Info,
} from 'lucide-react';
import { Scrim, Modal, IconBtn } from './primitives';

/* ============================================================
   Shared shell components — a styled-components port of the
   reference panel's chart + modal + chatbot primitives.
   Values (sizes, strokes, animation curves) are verbatim from
   the reference's Donut / StackBars / Ring / Modal / Chatbot.
   ============================================================ */

/* ---------- Icon (lucide map of the reference ICON set) ---------- */
const ICONS = {
  x: X, search: Search, bell: Bell, doc: FileText, sun: Sun, moon: Moon,
  sparkles: Sparkles, send: Send, shield: Shield, eye: Eye, logout: LogOut,
  chevR: ChevronRight, chevL: ChevronLeft, edit: Pencil, plus: Plus,
  users: Users, trash: Trash2, download: Download, calendar: Calendar,
  upload: Upload, assess: ClipboardList, target: Target, award: Award,
  enroll: GraduationCap, scores: BarChart3,
  // flow icons (onboarding / course wizard / workflow map)
  play: Play, globe: Globe, rocket: Rocket, mail: Mail, card: CreditCard,
  lock: Lock, copy: Copy, clock: Clock, layers: Layers, route: Route,
  check: Check, course: BookOpen, cog: Settings, settings: Settings,
  flag: Flag, dashboard: LayoutDashboard,
  down: ArrowDown, up: ArrowUp, branch: GitBranch, building: Building2,
  info: Info,
};
export function Icon({ name, size = 24, ...rest }) {
  const Cmp = ICONS[name] || Search;
  return <Cmp size={size} strokeWidth={2} {...rest} />;
}

/* ---------- Donut chart (SVG) — reference values ---------- */
export function Donut({ value, total, size = 132, stroke = 14, color, label = 'used' }) {
  const theme = useTheme();
  const strokeColor = color || theme.accent;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = total > 0 ? Math.min(value / total, 1) : 0;
  const [draw, setDraw] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setDraw(pct));
    return () => cancelAnimationFrame(id);
  }, [pct]);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={theme.surface3} strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={strokeColor} strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - draw)}
        style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.3,.7,.2,1)' }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%" y="48%" textAnchor="middle" dominantBaseline="middle" className="tnum"
        style={{ fontFamily: theme.fontHead, fontWeight: 780, fontSize: size * 0.24, fill: theme.text }}
      >{Math.round(pct * 100)}%</text>
      <text
        x="50%" y="64%" textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize: 11, fill: theme.textSubtle, fontWeight: 600 }}
      >{label}</text>
    </svg>
  );
}

/* ---------- Stacked bars — reference values ---------- */
export function StackBars({ data }) {
  const theme = useTheme();
  const PLOT = 188;
  const max = Math.max(...data.map((d) => d.v[0] + d.v[1]));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 200, paddingTop: 8 }}>
      {data.map((d) => {
        const newH = (d.v[0] / max) * PLOT;
        const compH = (d.v[1] / max) * PLOT;
        return (
          <div key={d.m} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, height: '100%', justifyContent: 'flex-end' }}>
            <div style={{ width: '100%', maxWidth: 46, display: 'flex', flexDirection: 'column-reverse', gap: 3, borderRadius: '6px 6px 0 0', overflow: 'hidden' }}>
              <div style={{ width: '100%', height: compH, background: `color-mix(in oklch, ${theme.accent} 36%, ${theme.surface3})` }} />
              <div style={{ width: '100%', height: newH, background: theme.accent }} />
            </div>
            <span style={{ fontSize: theme.fsMicro, color: theme.textSubtle, fontWeight: 600 }}>{d.m}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Progress ring — reference values ---------- */
export function Ring({ pct, size = 56, stroke = 7, color }) {
  const theme = useTheme();
  const strokeColor = color || theme.accent;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flex: 'none' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={theme.surface3} strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={strokeColor} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%" y="52%" textAnchor="middle" dominantBaseline="middle" className="tnum"
        style={{ fontSize: size * 0.27, fontWeight: 760, fill: theme.text, fontFamily: theme.fontHead }}
      >{pct}</text>
    </svg>
  );
}

/* ---------- Modal — Esc / X button to close. Outside (scrim) click does
   NOT close, to avoid accidental loss of in-progress form input. ---------- */
export function ModalShell({ title, sub, children, foot, onClose, max }) {
  useEffect(() => {
    const h = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const modal = (
    <Scrim>
      <Modal style={max ? { maxWidth: max } : null} onClick={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <div>
            <h3>{title}</h3>
            {sub && <p>{sub}</p>}
          </div>
          <IconBtn className="x-btn" onClick={onClose}><Icon name="x" size={18} /></IconBtn>
        </div>
        <div className="modal__body">{children}</div>
        {foot && <div className="modal__foot">{foot}</div>}
      </Modal>
    </Scrim>
  );

  return createPortal(modal, document.body);
}

/* ---------- Chatbot assistant — reference values ---------- */
const Fab = styled.button`
  position: fixed;
  right: 26px;
  bottom: 26px;
  width: 56px;
  height: 56px;
  border-radius: 18px;
  background: ${({ theme }) => `linear-gradient(150deg, ${theme.accent}, ${theme.accentStrong})`};
  color: ${({ theme }) => theme.onAccent};
  border: none;
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadowLg};
  z-index: 80;
  transition: transform .18s;
  &:hover { transform: translateY(-3px) scale(1.04); }
`;

const ChatPanel = styled.div`
  position: fixed;
  right: 26px;
  bottom: 94px;
  width: 358px;
  max-width: calc(100vw - 52px);
  height: 476px;
  max-height: calc(100vh - 130px);
  background: ${({ theme }) => theme.surface};
  border-radius: ${({ theme }) => theme.rXl};
  box-shadow: ${({ theme }) => theme.shadowLg};
  border: 1px solid ${({ theme }) => theme.border};
  z-index: 80;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .chatbot-head { padding: 16px 18px; background: ${({ theme }) => theme.surface2}; border-bottom: 1px solid ${({ theme }) => theme.border}; display: flex; align-items: center; gap: 11px; }
  .chatbot-body { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px; }
  .chatbot-foot { padding: 12px; border-top: 1px solid ${({ theme }) => theme.border}; display: flex; gap: 8px; }
`;

const Bubble = styled.div`
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 15px;
  font-size: 13.5px;
  line-height: 1.5;
  ${({ $me, theme }) => $me
    ? `background: ${theme.accent}; color: ${theme.onAccent}; border-bottom-right-radius: 5px; align-self: flex-end;`
    : `background: ${theme.surface3}; border-bottom-left-radius: 5px; align-self: flex-start; color: ${theme.text};`}
`;

export { Fab, ChatPanel, Bubble };
